import { useCallback, useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getLabelGroupListForWorkspaceQueryDocument } from "./label-group.gql";

export interface UseGetLabelGroupListForWorkspaceProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetLabelGroupListForWorkspace = ({
  workspaceId,
  enabled = true,
}: UseGetLabelGroupListForWorkspaceProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.labelGroups.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }

      const client = createOperationClient("GetLabelGroupListForWorkspace");
      const data = await client.request(getLabelGroupListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });

      return data.labelGroupListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const labelGroupList = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }

    return queryResult.data.slice().sort((a, b) => {
      if (!a || !b) {
        return 0;
      }
      return a.title.localeCompare(b.title);
    });
  }, [queryResult.data]);

  const findLabelGroupById = useCallback(
    (id: string) => {
      return labelGroupList.find((p) => p.id === id);
    },
    [labelGroupList],
  );

  return {
    loading: queryResult.isLoading,
    error: queryResult.error,
    labelGroupList,
    queryResult,
    findLabelGroupById,
  };
};
